import FingerprintJS from '@fingerprintjs/fingerprintjs';
import printJS from 'print-js';
import PerformanceUtils from 'services/PerformanceUtils';
import disconnectSound from 'assets/audio/disconnect.mp3';
import harpSound from 'assets/audio/harp.mp3';
import blopSound from 'assets/audio/blop.mp3';
import messageReceivedSound from 'assets/audio/message-received.mp3';
import swooshSound from 'assets/audio/swoosh.mp3';
import klaxonSound from 'assets/audio/klaxon.mp3';
import { BasicToastrOptions, toastr } from 'react-redux-toastr';
import config from '../config';
import { isPatientPortal } from 'services/PortalUtils';
import { toast } from 'components/Toasts';
import bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { using } from './TypeUtils';
import { getPreciseDistance } from 'geolib';

const bugsnagClient = bugsnag.start({
  apiKey: 'daa4dce0f1c2826d2b585c40e09804b2',
  plugins: [new BugsnagPluginReact()],
});

const COMMON_MIME_TYPES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/tiff': 'tif',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/x-wav': 'wav',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'application/pdf': 'pdf',
};

interface Notification {
  title: string;
  message: string;
}

interface ToastrOptions {
  attention?: boolean;
  timeOut?: number | string;
  removeOnHover?: boolean;
  showCloseButton?: boolean;
  closeOnToastrClick?: boolean;
  position?: string;
}

interface AddressLike {
  line1?: string;
  line2?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country?: string;
  notes?: string;
  latitude?: string;
  longitude?: string;
  [key: string]: any;
}

interface WithAddress {
  address?: AddressLike;
}

class MiscUtils {
  static isMacOS(): boolean {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }

  static printUrl(url: string): void {
    printJS(url);
  }

  static bytesToMegabytes(bytes: number): number {
    return bytes / 1000000;
  }

  static async readBlobWithFileReader(blob: Blob): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result));
      reader.addEventListener('error', reject);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * @param {Blob} blob
   */
  static getBlobUrl(blob: Blob | undefined): string | undefined {
    if (blob) {
      let urlCreator = window.URL || window.webkitURL;
      return urlCreator.createObjectURL(blob);
    }
  }

  static async getLocalUrlFromUrl(url: string): Promise<string | undefined> {
    return MiscUtils.urlToBlob(url).then((blob) => MiscUtils.getBlobUrl(blob));
  }

  static dataUrlToBlobUrl(dataUrl: string): string {
    const [typeInfo, base64Data] = dataUrl.split(',');
    const mimeType = typeInfo!.match(/data:(.*)/)?.[1] ?? '';
    const isBase64 = typeInfo!.indexOf('base64') >= 0;
    const decodedData = isBase64 ? atob(base64Data!) : decodeURIComponent(base64Data!);
    const byteNumbers = new Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      byteNumbers[i] = decodedData.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  static fileExtensionFromMime(mimeType: string): string | null {
    return COMMON_MIME_TYPES[mimeType] || null;
  }

  static async urlToBlob(url: string, type?: string): Promise<Blob> {
    let response = await fetch(url);
    const reader = response.body!.getReader();
    let chunks: BlobPart[] = [];
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    return new Blob(chunks, { type });
  }

  static async urlToFile(url: string, type: string, filename: string): Promise<File> {
    return fetch(url)
      .then((res) => res.blob()) // Gets the response and returns it as a blob
      .then((blob) => {
        return new File([blob], filename, { type });
      });
  }

  static playAudio(filename: string, volume: number = 0.25): void {
    const audio = new Audio(filename);
    audio.volume = volume;
    try {
      audio.play();
    } catch (err) {}
  }

  static playKlaxonSound(): void {
    MiscUtils.playAudio(klaxonSound, 1);
  }

  static playDisconnectSound(): void {
    MiscUtils.playAudio(disconnectSound);
  }

  static playNewChat(): void {
    MiscUtils.playAudio(blopSound);
  }

  static playSentDocumentSound(): void {
    MiscUtils.playAudio(swooshSound);
  }

  static playQueueSound(): void {
    MiscUtils.playAudio(harpSound);
  }

  static playMessageSound(): void {
    MiscUtils.playAudio(messageReceivedSound);
  }

  static showSuccess(title: string, text: string = title + ' success!'): void {
    if (isPatientPortal) {
      toast.success(text);
    } else {
      toastr.success(title, text);
    }
  }

  static showToastrError(title: string, message?: string): void {
    if (isPatientPortal) {
      toast.error(message!);
    } else {
      toastr.error(title, message!);
    }
  }

  static openExternalLink(url: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noreferrer noopener';
    a.click();
  }

  static getFullAddress(
    address: AddressLike | undefined,
    includeNotes: boolean = false,
    hideFields: string[] = [],
  ): string {
    if (address === undefined) return '';

    address = PerformanceUtils.clone(address);
    hideFields.forEach((field) => {
      delete address![field];
    });

    let fields: (string | undefined)[] = [
      address.line1,
      address.line2,
      address.postcode,
      address.city,
      address.state,
      address.country,
    ];

    if (includeNotes && address.notes) fields.push('Notes: ' + address.notes);

    fields = fields.filter((item) => item !== undefined && item !== '');
    return fields.join(', ');
  }

  static showBrowserNotification(notification: Notification): void {
    if (window.Notification && Notification.permission !== 'denied') {
      Notification.requestPermission((status) => {
        // status is "granted", if accepted by user
        if (status === 'granted' && window.document.hidden) {
          //Show browser native notification
          new Notification(notification.title, { body: notification.message });
        } else {
          MiscUtils.showToastrInfo(notification);
        }
      });
    } else {
      MiscUtils.showToastrInfo(notification);
    }
  }

  static getBrowserFingerprint(cb: (visitorId: string) => void): void {
    const fpPromise = FingerprintJS.load();
    fpPromise
      .then((fp) => fp.get())
      .then((result) => {
        cb(result.visitorId);
      });
  }

  static showClinicMessage(message: string): void {
    let options: ToastrOptions = {
      attention: true,
      timeOut: 'infinite',
      removeOnHover: false,
    };
    MiscUtils.showToastrInfo({ title: 'Care& Family Health Message', message }, options);
  }

  static showToastrInfo(
    notification: { title: string; message?: string },
    options: ToastrOptions = {
      timeOut: 15 * 1000,
      showCloseButton: false,
      closeOnToastrClick: true,
      position: 'bottom-center',
    },
  ): void {
    if (isPatientPortal) {
      toast.info(notification.message!, { duration: options.timeOut as number });
    } else {
      toastr.info(notification.title, notification.message!, options as BasicToastrOptions);
    }
  }

  static getBugsnagClient() {
    return bugsnagClient;
  }

  static cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
    return Object.entries(obj)
      .filter(([key, value]) => value !== null && value !== undefined)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}) as Partial<T>;
  }

  static isValidUrl(string: string): boolean {
    try {
      const _ = new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  }

  static getDistance(a: WithAddress | undefined, b: WithAddress | undefined): number | undefined {
    const aLatLng = {
      latitude: using(a?.address?.latitude, parseFloat),
      longitude: using(a?.address?.longitude, parseFloat),
    };
    const bLatLng = {
      latitude: using(b?.address?.latitude, parseFloat),
      longitude: using(b?.address?.longitude, parseFloat),
    };
    if (!aLatLng.latitude || !aLatLng.longitude || !bLatLng.latitude || !bLatLng.longitude) {
      return;
    }
    return getPreciseDistance(aLatLng as any, bLatLng as any);
  }
}

export default MiscUtils;
